import { Injectable } from '@angular/core'
import { ActivatedRoute, ActivationEnd, NavigationEnd, NavigationStart, Router } from '@angular/router'
import { Observable, of, pipe } from 'rxjs'
import { NavStateContainer } from '../models/nav-state-container'
import { JmlUrlParamService } from './jml-url-param.service'
import { Store, select } from '@ngrx/store'
import {
  PageStateActions,
  PageStateState,
  PageStateSelectors
} from '../../../root-store/page-state-store/page-state-index'
import { RootStoreState } from '../../../root-store/root-index'
import { filter, first, map, switchMap, take, tap } from 'rxjs/operators'
import TokenUtils from '../utils/token.utils'

export type TokenSources = 'found' | 'generated'
/**
 * This adapter is responsible for writing site state into and loading existing state from the store.
 */
@Injectable({
  providedIn: 'root'
})
export class JmlNavAdapterService {
  constructor(
    private jmlUrlParam: JmlUrlParamService,
    private store$: Store<RootStoreState.State>
  ) {}

  /** Loads a state container for a token from the store.
   * Accepts a default state as second parameter, if this method is called without checking first,
   * if the store has a state for the token.
   */
  loadStateForToken$<T>(token: string, defaultState?: NavStateContainer<T>): Observable<NavStateContainer<T>> {
    return this.store$.pipe(
      select(PageStateSelectors.selectStateForToken, { token }),
      take(1),
      // tap(pageStateBase => console.log('pageStateBase:', pageStateBase)),
      map((pageStateBase) =>
        !pageStateBase && !!defaultState ? defaultState : (pageStateBase as NavStateContainer<T>)
      ),
      map((pageState) => {
        if (!pageState) {
          return pageState
        }
        if (!pageState._compState && !!pageState.baseState) {
          // console.log('pageState:', pageState);
          pageState = pageState as NavStateContainer<T>
          // console.log('pagestate is of type: ', typeof pageState);
          pageState.reviveCompState()

          // console.log('pageState after revive:', pageState);
        }
        return pageState
      })
    )
  }

  /** Loads a state from the store for a given initial state. If store has no state for the releated token,
   * this method return the provided initial state.
   */
  // <T extends {}> is a workaround to define generic arrow methods. (https://stackoverflow.com/a/45576880/5536342)
  loadStateForStateObj$ = <T extends {}>(
    initialStateContainer: NavStateContainer<T>
  ): Observable<NavStateContainer<T>> =>
    this.loadStateForToken$(initialStateContainer.tokenObj.token, initialStateContainer)

  loadStateForNavId$<T>(id: number): Observable<NavStateContainer<T>> {
    // console.log('loading state via routing nav id.', id);
    return this.store$.pipe(
      select(PageStateSelectors.selectStateForNavId, { id }),
      map((pageStateBase) => pageStateBase as NavStateContainer<T>),
      map((pageState) => {
        if (!pageState) {
          return pageState
        }
        if (!pageState._compState && !!pageState.baseState) {
          // console.log('pageState:', pageState);
          pageState = pageState as NavStateContainer<T>
          // console.log('pagestate is of type: ', typeof pageState);
          pageState.reviveCompState()
          // return pageState.cloneAndReviveCompState();
        }
        return pageState
      })
    )
  }

  /** Saves the current state to the store */
  saveState<T>(newState: NavStateContainer<T>): void {
    // check if there is a state for the token in the store already.
    // console.log('saving state to store.', newState);
    this.hasStateForToken$(newState.tokenObj.token)
      .pipe(
        take(1),
        map((storeHasState) =>
          this.store$.dispatch(PageStateActions.siteStateChanged({ newState, isInitialState: !storeHasState }))
        )
      )
      .subscribe()
  }

  removeState(token: string): void {
    // console.log('removing state for token: ', token);
    this.hasStateForToken$(token)
      .pipe(
        first(),
        filter((hasToken) => hasToken),
        tap(() => this.store$.dispatch(PageStateActions.siteStateObsolete({ token })))
      )
      .subscribe()
  }
  /** Checks, if the store has saved state for a given token. */
  hasStateForToken$ = (token: string): Observable<boolean> =>
    this.store$.pipe(select(PageStateSelectors.selectTokenExists, { token }))

  hasStateForNavId$ = (navId: number): Observable<boolean> =>
    this.store$.pipe(select(PageStateSelectors.selectStateForNavIdExists, { id: navId }))

  /** Checks url for existing token. If url has no token, a token will be generated and added to the url.
   * return-value is a tuple. The first value states, if the token was freshly generated, the second value is the token itself.
   */
  readOrInitToken$(route: ActivatedRoute, router: Router): Observable<{ source: TokenSources; token: string }> {
    console.log('checking for token in url')
    return route.queryParamMap.pipe(
      map((params) => {
        const initToken = !params.has(TokenUtils.TOKEN_KEY)
        let token = ''
        let source = 'found' as TokenSources
        if (initToken) {
          // console.log('found no token in url.');
          token = TokenUtils.generateToken()
          console.log('generated token.', token)
          this.jmlUrlParam.writeToken(router, token)
          console.log('added token to url.')
          source = 'generated' as TokenSources
        } else {
          console.log('found token in url')
          token = params.get(TokenUtils.TOKEN_KEY)
        }

        const result = {
          source,
          token
        }
        // console.log('result:', result);
        return result
      })
    )
  }

  readToken$(route: ActivatedRoute): Observable<string> {
    return route.queryParamMap.pipe(
      filter((params) => params.has(TokenUtils.TOKEN_KEY)),
      map((params) => params.get(TokenUtils.TOKEN_KEY))
    )
  }

  hasToken$ = (route: ActivatedRoute): Observable<boolean> =>
    route.queryParamMap.pipe(map((params) => params.has(TokenUtils.TOKEN_KEY) && !!params.get(TokenUtils.TOKEN_KEY)))

  initToken = (router: Router): string => {
    const token = TokenUtils.generateToken()
    this.jmlUrlParam.writeToken(router, token)
    return token
  }

  checkToken(route: ActivatedRoute): void {
    // console.log('current token: ', )
    route.queryParamMap
      .pipe(
        take(1),
        map((params) => params.get(TokenUtils.TOKEN_KEY))
        // tap(token => console.log(`current token: ${token}`))
      )
      .subscribe()
  }

  writeTokenToUrl(router: Router, token: string): void {
    this.jmlUrlParam.writeToken(router, token)
  }

  setLastNavigation(navStart: NavigationStart, navEnd: NavigationEnd): void {
    this.store$.dispatch(PageStateActions.siteNavigation({ navStart, navEnd }))
  }

  getLastNavigation$(): Observable<{ navStart: NavigationStart; navEnd: NavigationEnd }> {
    return this.store$.pipe(select(PageStateSelectors.selectLastNavEventPair))
  }

  isThisBrowserNavigationAndStateExist$(event: NavigationEnd): Observable<boolean> {
    return this.store$.pipe(select(PageStateSelectors.selectStateForNavIdExists, { id: event.id }))
  }
  isBrowserNavigationAndStateExists2$(event: NavigationStart): Observable<boolean> {
    const orgNavId = event.restoredState['navigationId']
    return this.store$.pipe(select(PageStateSelectors.selectStateForNavIdExists, { id: orgNavId }))
  }

  stateForBrowserNavExists$(event: NavigationStart): Observable<boolean> {
    if (!event || !event.restoredState) {
      return of(false)
    }
    const orgNavId = event.restoredState['navigationId']
    return this.store$.pipe(select(PageStateSelectors.selectStateForNavIdExists, { id: orgNavId }))
  }

  getStateForRouteEventId<T>(event: NavigationStart): Observable<NavStateContainer<T>> {
    const orgNavId = event.restoredState['navigationId']
    return this.store$.pipe(
      select(PageStateSelectors.selectStateForNavId, { id: orgNavId }),
      map((state) => state as NavStateContainer<T>),
      // still necessary?
      map((pageState) => {
        if (!pageState) {
          return pageState
        }
        if (!pageState._compState && !!pageState.baseState) {
          console.log('pageState:', pageState)
          pageState = pageState as NavStateContainer<T>
          // console.log('pagestate is of type: ', typeof pageState);
          pageState.reviveCompState()
          // return pageState.cloneAndReviveCompState();
        }
        return pageState
      })
    )
  }
}
